import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { StaffAccount, StaffAccountCreate, StaffAccountUpdate } from '@garage/shared'
import { ALL_STAFF_SECTIONS, type StaffSection } from '@garage/shared'
import { Button, Dialog, TextField, Select, useSnackbar, ConfirmDialog, Spinner, Checkbox } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useAuth } from '@/components/AuthGate.js'
import { useFormatters } from '@/lib/hooks.js'

const SECTION_NAV_KEY: Record<StaffSection, string> = {
  jobcards: 'nav.jobcards',
  finance: 'nav.finance',
  accounting: 'nav.accounting',
  storage: 'nav.storage',
  suppliers: 'nav.suppliers',
  clients: 'nav.clients',
  workers: 'nav.staff',
  calendar: 'nav.calendar',
  catalog: 'nav.catalog',
  settings: 'nav.settings',
}

export function StaffAccountsSection() {
  const { account: currentAccount } = useAuth()
  const { t } = useTranslation()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()
  const formatters = useFormatters()

  const isAdmin = currentAccount.role === 'admin'

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editAccessDialog, setEditAccessDialog] = useState<StaffAccount | null>(null)
  const [tempPasswordDialog, setTempPasswordDialog] = useState<{ displayName: string; password: string } | null>(null)
  const [resetConfirmDialog, setResetConfirmDialog] = useState<StaffAccount | null>(null)
  const [removeConfirmDialog, setRemoveConfirmDialog] = useState<StaffAccount | null>(null)

  // Form state for create dialog
  const [createFormState, setCreateFormState] = useState<{
    username: string
    displayName: string
    role: 'admin' | 'staff'
    sections: StaffSection[]
    readOnly: boolean
  }>({
    username: '',
    displayName: '',
    role: 'staff',
    sections: [],
    readOnly: false,
  })
  const [createFormErrors, setCreateFormErrors] = useState<Record<string, string>>({})

  // Form state for edit access dialog
  const [editAccessFormState, setEditAccessFormState] = useState<{
    sections: StaffSection[]
    readOnly: boolean
  }>({
    sections: [],
    readOnly: false,
  })

  // Query for staff accounts list. Every hook in this component runs
  // unconditionally on every render (Rules of Hooks) — `enabled` is what
  // actually stops the request for a non-admin, not a conditional call, and
  // the "not an admin" branch below is a plain early return in the JSX, not
  // a hook.
  const { data: listData, isLoading: isLoadingList } = useQuery({
    queryKey: qk.staffAccounts,
    queryFn: () => api.staffAccounts.list(),
    enabled: isAdmin,
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (input: StaffAccountCreate) => {
      return api.staffAccounts.create(input)
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: qk.staffAccounts })
      setCreateDialogOpen(false)
      setCreateFormState({ username: '', displayName: '', role: 'staff', sections: [], readOnly: false })
      setCreateFormErrors({})
      snackbar.success(t('settings.staffAccounts.accountCreated'))
      // Show the temporary password dialog
      setTempPasswordDialog({
        displayName: result.displayName,
        password: result.temporaryPassword,
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        if (error.fieldErrors && Object.keys(error.fieldErrors).length > 0) {
          setCreateFormErrors(error.fieldErrors)
        }
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  // Update mutation (for toggling active/role)
  const updateMutation = useMutation({
    mutationFn: async (input: { account: StaffAccount; update: StaffAccountUpdate }) => {
      return api.staffAccounts.update(input.account.id, input.update)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.staffAccounts })
      snackbar.success(t('settings.staffAccounts.accountUpdated'))
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  // Edit access mutation
  const editAccessMutation = useMutation({
    mutationFn: async (input: { account: StaffAccount; update: { sections: StaffSection[]; readOnly: boolean } }) => {
      return api.staffAccounts.update(input.account.id, input.update)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.staffAccounts })
      setEditAccessDialog(null)
      snackbar.success(t('settings.staffAccounts.accountUpdated'))
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.staffAccounts.resetPassword(id)
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: qk.staffAccounts })
      setResetConfirmDialog(null)
      snackbar.success(t('settings.staffAccounts.passwordReset'))
      // Show the temporary password dialog
      setTempPasswordDialog({
        displayName: result.displayName,
        password: result.temporaryPassword,
      })
    },
    onError: (error) => {
      setResetConfirmDialog(null)
      if (error instanceof ApiError) {
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  // Remove mutation
  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.staffAccounts.remove(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.staffAccounts })
      setRemoveConfirmDialog(null)
      snackbar.success(t('settings.staffAccounts.accountRemoved'))
    },
    onError: (error) => {
      setRemoveConfirmDialog(null)
      if (error instanceof ApiError) {
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const handleCreateSubmit = () => {
    setCreateFormErrors({})
    const input: StaffAccountCreate = {
      username: createFormState.username.trim(),
      displayName: createFormState.displayName.trim(),
      role: createFormState.role,
      sections: createFormState.role === 'admin' ? [] : createFormState.sections,
      readOnly: createFormState.role === 'admin' ? false : createFormState.readOnly,
    }
    createMutation.mutate(input)
  }

  const handleToggleActive = (account: StaffAccount) => {
    updateMutation.mutate({
      account,
      update: { active: !account.active },
    })
  }

  const handleToggleRole = (account: StaffAccount) => {
    const newRole = account.role === 'admin' ? 'staff' : 'admin'
    updateMutation.mutate({
      account,
      update: { role: newRole as 'admin' | 'staff' },
    })
  }

  const handleOpenEditAccess = (account: StaffAccount) => {
    setEditAccessFormState({
      sections: account.sections,
      readOnly: account.readOnly,
    })
    setEditAccessDialog(account)
  }

  const handleSaveEditAccess = () => {
    if (!editAccessDialog) return
    editAccessMutation.mutate({
      account: editAccessDialog,
      update: {
        sections: editAccessFormState.sections,
        readOnly: editAccessFormState.readOnly,
      },
    })
  }

  const handleResetPassword = (account: StaffAccount) => {
    setResetConfirmDialog(account)
  }

  const handleConfirmReset = () => {
    if (resetConfirmDialog) {
      resetPasswordMutation.mutate(resetConfirmDialog.id)
    }
  }

  const handleRemove = (account: StaffAccount) => {
    setRemoveConfirmDialog(account)
  }

  const handleConfirmRemove = () => {
    if (removeConfirmDialog) {
      removeMutation.mutate(removeConfirmDialog.id)
    }
  }

  const handleCopyPassword = async () => {
    if (!tempPasswordDialog) return
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(tempPasswordDialog.password)
        snackbar.success(t('customerLinkPanel.linkCopied'))
      }
    } catch {
      snackbar.error(t('customerLinkPanel.couldNotCopy'))
    }
  }

  // All hooks above have now run unconditionally, so it is safe to branch —
  // a non-admin sees nothing here at all, not a disabled version of it.
  if (!isAdmin) return null

  if (isLoadingList) {
    return (
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <h2 className="text-title-md text-on-surface">{t('settings.staffAccounts.title')}</h2>
        <p className="text-body-sm text-on-surface-variant mt-0.5 mb-4">{t('settings.staffAccounts.subtitle')}</p>
        <Spinner />
      </div>
    )
  }

  const items = listData?.items ?? []

  return (
    <>
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-title-md text-on-surface">{t('settings.staffAccounts.title')}</h2>
            <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.staffAccounts.subtitle')}</p>
          </div>
          <Button
            variant="filled"
            size="sm"
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('settings.staffAccounts.addAccount')}
          </Button>
        </div>

        {items.length === 0 ? (
          // Not reachable in practice — the admin viewing this page is
          // themselves a row in this list — but kept honest rather than
          // reusing an unrelated "loading" string if it ever is.
          <p className="text-body-sm text-on-surface-variant mt-4">—</p>
        ) : (
          <div className="mt-4 space-y-3">
            {items.map((account) => (
              <div
                key={account.id}
                className="flex items-center justify-between flex-wrap gap-y-2 p-3 bg-surface-high rounded-md border border-outline-variant"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-body-md text-on-surface font-medium">{account.displayName}</p>
                    <p className="text-body-sm text-on-surface-variant">({account.username})</p>
                    <span className="text-body-sm px-2 py-1 rounded bg-outline text-on-surface">
                      {account.role === 'admin' ? t('settings.staffAccounts.roleAdmin') : t('settings.staffAccounts.roleStaff')}
                    </span>
                    <span
                      className={`text-body-sm px-2 py-1 rounded ${
                        account.active ? 'bg-primary/20 text-primary' : 'bg-outline text-on-surface-variant'
                      }`}
                    >
                      {account.active ? t('settings.staffAccounts.statusActive') : t('settings.staffAccounts.statusDisabled')}
                    </span>
                    {account.role === 'admin' ? (
                      <span className="text-body-sm px-2 py-1 rounded bg-primary/20 text-primary">
                        {t('settings.staffAccounts.fullAccess')}
                      </span>
                    ) : (
                      <>
                        {account.sections.length === 0 ? (
                          <span className="text-body-sm px-2 py-1 rounded bg-outline text-on-surface-variant">
                            {t('settings.staffAccounts.sectionsNone')}
                          </span>
                        ) : (
                          <span className="text-body-sm px-2 py-1 rounded bg-outline text-on-surface-variant">
                            {t('settings.staffAccounts.sectionCount', { count: account.sections.length })}
                          </span>
                        )}
                        {account.readOnly && (
                          <span className="text-body-sm px-2 py-1 rounded bg-outline text-on-surface-variant">
                            {t('settings.staffAccounts.readOnly')}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <p className="text-body-sm text-on-surface-variant mt-2">
                    {t('settings.staffAccounts.lastSignIn')}: {account.lastLoginAt ? formatters.dateTime(account.lastLoginAt) : t('settings.staffAccounts.never')}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 min-w-0 md:ml-4 md:flex-shrink-0">
                  <button
                    onClick={() => handleToggleActive(account)}
                    disabled={updateMutation.isPending || resetPasswordMutation.isPending || editAccessMutation.isPending || removeMutation.isPending}
                    className="px-3 py-2 text-label-sm rounded-full border border-outline text-on-surface interactive hover:bg-surface-high active:bg-surface-high disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {account.active ? t('settings.staffAccounts.disable') : t('settings.staffAccounts.enable')}
                  </button>

                  <button
                    onClick={() => handleToggleRole(account)}
                    disabled={updateMutation.isPending || resetPasswordMutation.isPending || editAccessMutation.isPending || removeMutation.isPending}
                    className="px-3 py-2 text-label-sm rounded-full border border-outline text-on-surface interactive hover:bg-surface-high active:bg-surface-high disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {account.role === 'admin' ? t('settings.staffAccounts.makeStaff') : t('settings.staffAccounts.makeAdmin')}
                  </button>

                  {account.role !== 'admin' && (
                    <button
                      onClick={() => handleOpenEditAccess(account)}
                      disabled={updateMutation.isPending || resetPasswordMutation.isPending || editAccessMutation.isPending || removeMutation.isPending}
                      className="px-3 py-2 text-label-sm rounded-full border border-outline text-on-surface interactive hover:bg-surface-high active:bg-surface-high disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('settings.staffAccounts.editAccess')}
                    </button>
                  )}

                  <button
                    onClick={() => handleResetPassword(account)}
                    disabled={updateMutation.isPending || resetPasswordMutation.isPending || editAccessMutation.isPending || removeMutation.isPending}
                    className="px-3 py-2 text-label-sm rounded-full border border-outline text-on-surface interactive hover:bg-surface-high active:bg-surface-high disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('settings.staffAccounts.resetPassword')}
                  </button>

                  <button
                    onClick={() => handleRemove(account)}
                    disabled={updateMutation.isPending || resetPasswordMutation.isPending || editAccessMutation.isPending || removeMutation.isPending}
                    className="px-3 py-2 text-label-sm rounded-full border border-error text-error interactive hover:bg-error/10 active:bg-error/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('settings.staffAccounts.remove')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Account Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        title={t('settings.staffAccounts.newAccountTitle')}
      >
        <div className="space-y-4">
          <TextField
            label={t('settings.staffAccounts.username')}
            value={createFormState.username}
            onChange={(v) => {
              setCreateFormState(prev => ({ ...prev, username: v }))
              if (createFormErrors.username) {
                setCreateFormErrors(prev => {
                  const next = { ...prev }
                  delete next.username
                  return next
                })
              }
            }}
            supportingText={t('settings.staffAccounts.usernameHelp')}
            error={createFormErrors.username}
            fullWidth
            autoFocus
          />

          <TextField
            label={t('settings.staffAccounts.displayName')}
            value={createFormState.displayName}
            onChange={(v) => {
              setCreateFormState(prev => ({ ...prev, displayName: v }))
              if (createFormErrors.displayName) {
                setCreateFormErrors(prev => {
                  const next = { ...prev }
                  delete next.displayName
                  return next
                })
              }
            }}
            fullWidth
          />

          <Select
            label={t('settings.staffAccounts.role')}
            value={createFormState.role}
            onChange={(v: string) => setCreateFormState(prev => ({ ...prev, role: v as 'admin' | 'staff' }))}
            options={[
              { value: 'admin', label: t('settings.staffAccounts.roleAdmin') },
              { value: 'staff', label: t('settings.staffAccounts.roleStaff') },
            ]}
            fullWidth
          />

          {createFormState.role === 'admin' ? (
            <p className="text-body-sm text-on-surface-variant italic">
              {t('settings.staffAccounts.adminAlwaysFullAccess')}
            </p>
          ) : (
            <>
              <div>
                <label className="text-label-lg text-on-surface block mb-2">
                  {t('settings.staffAccounts.sections')}
                </label>
                <p className="text-body-sm text-on-surface-variant mb-3">
                  {t('settings.staffAccounts.sectionsHelp')}
                </p>
                <div className="space-y-2 ml-2">
                  {ALL_STAFF_SECTIONS.map(section => (
                    <label key={section} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={createFormState.sections.includes(section)}
                        onChange={(checked) => {
                          setCreateFormState(prev => ({
                            ...prev,
                            sections: checked
                              ? [...prev.sections, section]
                              : prev.sections.filter(s => s !== section),
                          }))
                        }}
                      />
                      <span className="text-body-sm text-on-surface">
                        {t(SECTION_NAV_KEY[section])}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={createFormState.readOnly}
                    onChange={(checked) => {
                      setCreateFormState(prev => ({ ...prev, readOnly: checked }))
                    }}
                  />
                  <div className="flex-1">
                    <span className="text-label-lg text-on-surface block">
                      {t('settings.staffAccounts.readOnly')}
                    </span>
                    <p className="text-body-sm text-on-surface-variant">
                      {t('settings.staffAccounts.readOnlyHelp')}
                    </p>
                  </div>
                </label>
              </div>
            </>
          )}

          <div className="flex gap-2 justify-end">
            <Button
              variant="outlined"
              onClick={() => setCreateDialogOpen(false)}
              disabled={createMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="filled"
              onClick={handleCreateSubmit}
              loading={createMutation.isPending}
            >
              {t('settings.staffAccounts.create')}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Temporary Password Dialog */}
      {tempPasswordDialog && (
        <Dialog
          open={!!tempPasswordDialog}
          onClose={() => setTempPasswordDialog(null)}
          title={t('settings.staffAccounts.temporaryPasswordTitle')}
        >
          <div className="space-y-4">
            <p className="text-body-sm text-on-surface">
              {t('settings.staffAccounts.temporaryPasswordBody', { name: tempPasswordDialog.displayName })}
            </p>

            <div className="bg-surface-high rounded-md p-4 border border-outline-variant">
              <p className="font-mono text-body-md text-on-surface break-all select-all">
                {tempPasswordDialog.password}
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outlined"
                onClick={handleCopyPassword}
              >
                {t('customerLinkPanel.copy')}
              </Button>
              <Button
                variant="filled"
                onClick={() => setTempPasswordDialog(null)}
              >
                {t('settings.staffAccounts.gotIt')}
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      {/* Reset Password Confirm Dialog */}
      <ConfirmDialog
        open={!!resetConfirmDialog}
        title={t('settings.staffAccounts.resetPassword')}
        message={resetConfirmDialog ? t('settings.staffAccounts.resetPasswordConfirm', { name: resetConfirmDialog.displayName }) : ''}
        confirmLabel={t('settings.staffAccounts.resetPassword')}
        onConfirm={handleConfirmReset}
        onCancel={() => setResetConfirmDialog(null)}
        loading={resetPasswordMutation.isPending}
      />

      {/* Remove Account Confirm Dialog */}
      <ConfirmDialog
        open={!!removeConfirmDialog}
        title={t('settings.staffAccounts.remove')}
        message={removeConfirmDialog ? t('settings.staffAccounts.removeConfirm', { name: removeConfirmDialog.displayName }) : ''}
        confirmLabel={t('settings.staffAccounts.remove')}
        destructive
        onConfirm={handleConfirmRemove}
        onCancel={() => setRemoveConfirmDialog(null)}
        loading={removeMutation.isPending}
      />

      {/* Edit Access Dialog */}
      <Dialog
        open={!!editAccessDialog}
        onClose={() => setEditAccessDialog(null)}
        title={t('settings.staffAccounts.accessFor', { name: editAccessDialog?.displayName || '' })}
      >
        <div className="space-y-4">
          <div>
            <label className="text-label-lg text-on-surface block mb-2">
              {t('settings.staffAccounts.sections')}
            </label>
            <p className="text-body-sm text-on-surface-variant mb-3">
              {t('settings.staffAccounts.sectionsHelp')}
            </p>
            <div className="space-y-2 ml-2">
              {ALL_STAFF_SECTIONS.map(section => (
                <label key={section} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={editAccessFormState.sections.includes(section)}
                    onChange={(checked) => {
                      setEditAccessFormState(prev => ({
                        ...prev,
                        sections: checked
                          ? [...prev.sections, section]
                          : prev.sections.filter(s => s !== section),
                      }))
                    }}
                  />
                  <span className="text-body-sm text-on-surface">
                    {t(SECTION_NAV_KEY[section])}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={editAccessFormState.readOnly}
                onChange={(checked) => {
                  setEditAccessFormState(prev => ({ ...prev, readOnly: checked }))
                }}
              />
              <div className="flex-1">
                <span className="text-label-lg text-on-surface block">
                  {t('settings.staffAccounts.readOnly')}
                </span>
                <p className="text-body-sm text-on-surface-variant">
                  {t('settings.staffAccounts.readOnlyHelp')}
                </p>
              </div>
            </label>
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              variant="outlined"
              onClick={() => setEditAccessDialog(null)}
              disabled={editAccessMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="filled"
              onClick={handleSaveEditAccess}
              loading={editAccessMutation.isPending}
            >
              {t('settings.staffAccounts.save')}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  )
}
